import { formatDate } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Table } from "primeng/table";
import { cloneDeep } from "lodash";
import { CheckboxChangeEvent } from "primeng/checkbox";
import { ProgressSpinnerMode } from "@angular/material/progress-spinner";
import { StateService } from "../../services/state.service";
import {
  myExperimentOverlayOptions,
  sharedExperimentOverlayOptions,
} from "../../main-container/studio/studio-constants";
import { DASHBOARD_TYPES } from "../../main-container/dashboard/dashboard-landing/dashboard-landing-view-type";
import { CatExpertRouterLink } from "../../app-routing-enum";
import { environment } from "../../../environments/environment.qa";

@Component({
  selector: "app-experiment-list",
  templateUrl: "./experiment-list.component.html",
  styleUrl: "./experiment-list.component.scss",
})
export class ExperimentListComponent implements OnInit {
  @ViewChild("expTbl") expTbl!: Table;
  @Input() tableColumns: any[] = [];
  @Input() tableData: any[] = [];
  @Input() allData: any[] = [];
  @Input() skeletonLoader: boolean = false;
  @Input() showFilters: boolean = false;
  @Input() paginationFirst: number = 0;
  @Input() pageSize: number = 20;
  @Input() totalPages: number = 1;
  @Input() currentPage: number = 1;
  @Input() totalItems: number = 0;
  @Input() dragDropDisabled: boolean = false;
  @Input() statusOptions = [];
  @Input() moveDialogView: boolean = false;
  needsExtraStatus = ["cloning", "queued"];
  @Input() showAccordion: boolean = false;
  @Input() isClientSide: boolean = false; // For handling client side actions like sorting, pagination etc..
  @Input() noRowClick: boolean = false; // For handling client side actions like sorting, pagination etc..
  @Input() styleClass: string = "";
  @Input() showPagination: boolean = true;

  @Input() dragDropEnabled: boolean = false;
  @Input() scrollFlag: boolean = false;
  @Input() scrollHeight: string = "";
  @Input() isSelectAndExclude: boolean = false;
  excludeIds: any = [];
  selectedRows: any = [];
  @Input() excludeSelectAll: boolean = false;
  @Input() clearSelectAll: boolean = false;

  @Output() rowDropped = new EventEmitter<{
    dragData: any;
    dropData: number;
  }>();
  @Output() emitTableAction = new EventEmitter<any>();
  @Input() accordionTemplate!: TemplateRef<any>;
  @Input() sibilingTableColSpan: number = 0;

  skeletonData = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  pagination = {
    limit: 20,
    options: [10, 20, 30, 50, 100],
  };
  first = 1;
  firstCount = 1;
  lastCount = 20;

  dragingData: any = {};

  actionColumnList: any = [];
  overlayDetails: any = {};
  selectedItems: any[] = [];
  orginalData: any = {};
  filterSuggestions: any[] = [];
  currentRoute: string = "";

  experimentNameMaxLength: number = 50;
  mode: ProgressSpinnerMode = "determinate";

  lastSelectedIndex: number | null = null;
  hoveredRow: any = null;

  allSelected: boolean = false;

  sortField: string = "created_at";
  sortOrder: number = -1;

  expandedRows: Set<any> = new Set();
  imageUrlPrefix = environment.imageUrl;

  dashboardTypes = DASHBOARD_TYPES;

  isSharedRoute: boolean = false;

  constructor(public stateService: StateService) {}

  ngOnInit(): void {
    this.currentRoute =
      this.stateService.getActiveRouteSegment() ?? CatExpertRouterLink.Studio;

    this.isSharedRoute = this.currentRoute === CatExpertRouterLink.Shared;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.tableData) {
      this.tableData = changes["tableData"].currentValue || [];

      this.skeletonLoader = false;

      this.handlePaginationData();
    }
    if (changes && changes.showAccordion) {
      this.showAccordion = changes["showAccordion"].currentValue;
    }
    if (changes && changes?.dragDropEnabled) {
      this.dragDropEnabled = changes?.dragDropEnabled?.currentValue || false;
    }

    this.clearTableSelection();

    // clearing table on clear filters
    if (changes && changes.showFilters) {
      if (this.isClientSide) {
        this.resetFiltersClientSide();
      } else {
        this.expTbl?.clear();
      }
    }

    if (this.isSelectAndExclude) {
      if (changes && changes.excludeSelectAll) {
        this.clearSelectAndExclude();
        this.excludeSelectAll =
          changes?.excludeSelectAll?.currentValue || false;
        this.allSelected = this.excludeSelectAll;
        if (!this.allSelected) {
          this.toggleAllSelection({ checked: false });
        } else {
          this.toggleAllSelection({ checked: true });
        }
      }
      if (changes && changes.tableData) {
        if (!this.excludeIds?.length && this.isSelectAndExclude) {
          this.allSelected = this.excludeSelectAll;
        }
        if (this.excludeSelectAll) {
          this.toggleAllSelection({ checked: true });
        } else {
          this.clearSelectAndExclude();
        }
      }
    }
  }

  handlePaginationData() {
    if (this.isClientSide) {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
      this.tableData = this.allData.slice(start, end);
    }

    this.firstCount =
      this.currentPage === 1
        ? this.currentPage
        : (this.currentPage - 1) * this.pageSize + 1;
    this.lastCount =
      this.currentPage * this.pageSize < this.totalItems
        ? this.currentPage * this.pageSize
        : this.totalItems;
  }

  toggleSort(column: any) {
    if (this.sortField === column.value) {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    } else {
      this.sortField = column.value;
      this.sortOrder = -1;
    }

    // client-side sorting
    if (this.isClientSide) {
      if (this.tableData && this.sortField) {
        this.tableData = this.allData.sort((a: any, b: any) =>
          this.compareValues(
            a[this.sortField],
            b[this.sortField],
            this.sortOrder
          )
        );
      }
      return;
    }

    let option = {
      key: "sort",
      callBack: "sortingAction",
    };

    let sortInput = {
      sort: this.sortField
        ? [
            {
              field: this.sortField,
              direction: this.sortOrder === 1 ? "asc" : "desc",
            },
          ]
        : [],
      page: this.currentPage,
      page_size: this.pageSize,
    };

    this.emitTableAction.emit({ action: option, data: sortInput });
  }

  private compareValues(a: any, b: any, sortOrder: number): number {
    const toNumberSafe = (val: any): number | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === "number") {
        return isNaN(val) ? null : val;
      }
      if (typeof val === "string") {
        const s = val.trim();
        if (s === "" || s === "-") return null;
        // remove common thousands separators
        const cleaned = s.replace(/,/g, "");
        const n = Number(cleaned);
        return isNaN(n) ? null : n;
      }
      return null;
    };

    const aNum = toNumberSafe(a);
    const bNum = toNumberSafe(b);

    // Null-like values go last
    if (aNum === null && bNum === null) {
      // If neither parses to a number, fallback to localized string compare
      return (
        String(a ?? "").localeCompare(String(b ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * sortOrder
      );
    }
    if (aNum === null) return 1; // push a to bottom
    if (bNum === null) return -1; // push b to bottom

    // Both parsed as numbers -> numeric compare
    if (aNum !== null && bNum !== null) {
      if (aNum === bNum) return 0;
      return sortOrder * (aNum - bNum);
    }

    // Fallback string compare
    return (
      sortOrder *
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }

  isSortedColumn(column: any): boolean {
    return this.sortField === column.value;
  }

  isAscending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === 1;
  }

  isDescending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === -1;
  }

  getString(val: any, column?: any) {
    if (column && (column?.value === "status" || column?.value === "type")) {
      let textVal = this.stateService.titleRemoveUnderscore(val);
      if (typeof textVal === "string") {
        return textVal != "" ? textVal : "";
      }
      return;
    }
    if (column && column.value === "experiment_type") {
      return val === "space_assortment" ? "Space Planogram" : val;
    }
    if (typeof val === "string") {
      return val != "" ? val : "";
    }

    return val != null ? val : "";
  }

  onDragStart(event: DragEvent, data: any) {
    if (this.dragDropDisabled) {
      return;
    }
    this.dragingData = data;
    // Create custom ghost element
    const ghost = document.createElement("div");
    ghost.innerText = data.name; // Customize text
    ghost.className = "experiment-drag-preview"; // Style this in CSS
    document.body.appendChild(ghost);

    // Make it hidden but still usable as drag image
    ghost.style.position = "absolute";
    ghost.style.top = "-1000px";
    ghost.style.left = "-1000px";

    // Use as drag image
    event.dataTransfer?.setDragImage(ghost, 10, 10);

    // Cleanup after short delay
    setTimeout(() => document.body.removeChild(ghost), 0);
  }

  // Helper method to find hover row data to add the style
  onRowDragOver(event: any, rowData: any) {
    if (this.dragDropDisabled) {
      return;
    }
    event.preventDefault(); // Must allow drop
    this.hoveredRow = rowData;
  }

  // Helper method to call drop the selected row on the folder
  onDropRow(event: any, dropRowData: any) {
    if (this.dragDropDisabled) {
      return;
    }
    if (
      dropRowData.experiment_type !== "folder" ||
      dropRowData === this.dragingData
    ) {
      this.dragingData = {};
      this.hoveredRow = null;
      return;
    }
    const draggedRow = this.dragingData;

    this.rowDropped.emit({
      dragData: draggedRow,
      dropData: dropRowData,
    });
    this.hoveredRow = null;
    this.dragingData = {};
  }

  isRowFolderType(row: any): boolean {
    return row.experiment_type === "folder";
  }

  toggleOverlay(actionColumn: any, data: any): void {
    const isShared = data?.hasShared;
    const isFolder = data?.experiment_type === "folder";
    const isSharedRoute = this.currentRoute === CatExpertRouterLink.Shared;

    if (isShared) {
      this.actionColumnList = cloneDeep(sharedExperimentOverlayOptions);
    } else if (isSharedRoute) {
      this.actionColumnList = cloneDeep(myExperimentOverlayOptions);
    } else {
      this.actionColumnList = cloneDeep(actionColumn.actionList || []);
    }

    if (isFolder) {
      this.actionColumnList.forEach((item: any) => {
        if (item?.key === "clone") {
          item.disable = true;
        }
      });
    }

    this.overlayDetails = data;
    this.clearTableSelection();
  }

  toSelectedExperiment(experiment: any, openInNewTab: boolean = false) {
    // If it's a dashboard type, use the specific callback
    if (this.dashboardTypes.includes(experiment?.type)) {
      this.actionCallback(
        { key: "view", callBack: "navigateToDashboard" },
        experiment
      );
      return;
    }
    let option = {
      label: "Open New Tab",
      key: openInNewTab,
      callBack: "navigateExperiment",
    };
    this.emitTableAction.emit({ action: option, data: experiment });
  }

  experimentAction(option: any) {
    this.emitTableAction.emit({ action: option, data: this.overlayDetails });
  }

  toggleAllSelection(event: CheckboxChangeEvent): void {
    if (event.checked) {
      // Select only items with hasAdded === false OR hasSynced === false(Product library master catelog selection) OR isSharedFolder === false(shared experiments selection)
      this.selectedItems = this.tableData.filter((item) =>
        this.isSharedRoute
          ? !item?.isSharedFolder
          : (!item?.hasAdded || !item?.hasSynced) &&
            (this.excludeSelectAll ? !this.excludeIds.includes(item.sku) : true)
      );
      this.selectedRows = [...this.selectedItems];
    } else {
      this.selectedItems = []; // Deselect all
      this.selectedRows = [];
    }

    this.excludeIds = this.excludeSelectAll ? this.excludeIds : [];

    this.handleSelectAndExcludeEmit();
    this.emitMultipleRowData();
  }

  // Handle select and exclude emit
  handleSelectAndExcludeEmit() {
    if (this.isSelectAndExclude) {
      const option = {
        key: "select-all",
        page: this.currentPage,
        enable: this.allSelected,
      };
      this.emitTableAction.emit({ action: option, data: this.selectedRows });
    }
  }

  // Helper method clear the table selection
  clearTableSelection() {
    this.allSelected = false;
    this.selectedItems = [];
    this.emitMultipleRowData();
  }

  clearSelectAndExclude() {
    this.excludeSelectAll = false;
    this.allSelected = false;
    this.selectedRows = [];
    this.toggleAllSelection({ checked: false });
  }

  isRowSelected(row: any): boolean {
    if (this.isSelectAndExclude) {
      return this.allSelected
        ? true
        : this.excludeSelectAll
          ? !this.excludeIds.includes(row.sku)
          : this.selectedRows.some((r: any) => r.id === row.id);
    } else {
      return this.selectedItems.some((r: any) => r.id === row.id); // assuming `id` is unique
    }
  }

  get selectionModel() {
    return this.isSelectAndExclude ? this.selectedRows : this.selectedItems;
  }

  toggleRowSelection(event: CheckboxChangeEvent, row: any): void {
    const nativeEvent = event.originalEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
    }

    if (this.isSelectAndExclude) {
      const existingRowIndex = this.selectedRows.findIndex(
        (r: any) => r.id === row.id
      );
      if (existingRowIndex !== -1) {
        this.selectedRows.splice(existingRowIndex, 1);
      } else {
        this.selectedRows.push(row);
      }

      if (this.excludeSelectAll) {
        let existingIndex = this.excludeIds.findIndex(
          (r: string) => r === row.sku
        );
        if (existingIndex !== -1) {
          this.excludeIds.splice(existingIndex, 1);
        } else {
          this.excludeIds.push(row.sku);
        }
      }
    } else {
      const existingIndex = this.selectedItems.findIndex(
        (r) => r.id === row.id
      );
      if (existingIndex !== -1) {
        this.selectedItems.splice(existingIndex, 1);
      } else {
        this.selectedItems.push(row);
      }
    }

    this.allSelected = this.selectedItems.length === this.tableData.length;
    this.handleSelectAndExcludeEmit();
    this.emitMultipleRowData();
  }

  toggleRowSelectionClientSide(event: CheckboxChangeEvent, row: any) {
    const nativeEvent = event.originalEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
    }

    // Set isChecked from event.checked
    row.isChecked = event.checked;

    const existingIndex = this.selectedItems.findIndex((r) => r.id === row.id);

    if (event.checked) {
      // Add to selectedItems if not already present
      if (existingIndex === -1) {
        this.selectedItems.push(row);
      }
    }

    this.emitMultipleRowData(); // Emit updated list
  }

  emitMultipleRowData() {
    const option = {
      key: this.selectedItems.length,
      callBack: "multipleActions",
    };

    let excludePayload: any = { exclude_ids: [], exclude: false };
    if (this.isSelectAndExclude) {
      excludePayload.exclude_ids = this.excludeIds;
      excludePayload.exclude = true;
    }
    this.emitTableAction.emit({
      action: option,
      data: this.selectedItems,
      rows: this.selectedRows,
      ...(this.isSelectAndExclude && this.excludeSelectAll
        ? excludePayload
        : {}),
    });
  }

  formatHumanReadableDate(dateStr: string): string {
    const inputDateObj = new Date(dateStr + "Z");
    const now = new Date();

    const inputDay = new Date(
      inputDateObj.getFullYear(),
      inputDateObj.getMonth(),
      inputDateObj.getDate()
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let label: string;

    if (inputDay.getTime() === today.getTime()) {
      label = "Today";
    } else if (inputDay.getTime() === yesterday.getTime()) {
      label = "Yesterday";
    } else {
      // Optional: format older dates like "Jul 7, 2025"
      return formatDate(inputDateObj, "MMM d, y, h:mm a", "en-US");
    }

    const time = formatDate(inputDateObj, "h:mm a", "en-US");
    return `${label}, ${time}`;
  }

  enableEditing(experiment: any, event: any) {
    event.stopPropagation();
    this.orginalData = cloneDeep(experiment);
    experiment.isEditable = true;
  }

  disableEditing(experiment: any, event: any) {
    event.stopPropagation();
    experiment.isEditable = false;
    experiment.name = this.orginalData.name;
    event.target.value = "";
  }

  editExperimentAction(experiment: any, event: any) {
    event.stopPropagation();
    if (experiment?.name === this.orginalData.name) {
      this.disableEditing(experiment, event);
      return;
    }
    if (
      !event.target.value &&
      experiment?.name.length > this.experimentNameMaxLength
    ) {
      return;
    }
    let option = {
      key: "edit",
      callBack: "renameExperiment",
    };
    this.emitTableAction.emit({ action: option, data: experiment });
    experiment.isEditable = false;
    event.target.value = "";
  }

  findFilterOptions(event: any, filterOptions: any) {
    if (event) {
      this.filterSuggestions = filterOptions.filter((each: any) => {
        if (each.value.toLowerCase().includes(event.query.toLowerCase())) {
          return each;
        }
      });
    }
  }

  clearAutocompleteFilters() {
    this.expTbl.clear();
  }

  onNgSelectChange(selectedItems: any[], column: any) {
    const selectedValues = Array.isArray(selectedItems)
      ? selectedItems.filter((v) => v !== undefined && v !== null)
      : [];

    let option = {
      key: column.value,
      callBack: "filterAction",
    };

    this.emitTableAction.emit({ action: option, data: selectedValues });
  }

  onNgSelectChangeClientSide(selectedItems: any[], column: any) {
    const selectedValues = Array.isArray(selectedItems)
      ? selectedItems.filter((v) => v !== undefined && v !== null)
      : [];

    const key = column?.value;

    // If nothing is selected, show all data
    if (!selectedValues.length) {
      this.tableData = [...this.allData];
      return;
    }

    // Filter client-side
    this.tableData = this.allData.filter((item: any) => {
      const cellValue = item?.[key];
      return selectedValues.includes(cellValue);
    });
  }

  filterSearch(value: string, column: any) {
    let option = {
      key: column.value,
      callBack: "filterSearchAction",
    };
    this.emitTableAction.emit({ action: option, data: value });
  }

  filterSearchClientSide(value: string, column: any) {
    const searchValue = value?.toString().trim().toLowerCase() || "";
    const key = column?.value;

    if (!searchValue || searchValue.length) {
      this.tableData = this.allData.filter((item: any) => {
        const cellValue = String(item?.[key] ?? "").toLowerCase();
        return cellValue.includes(searchValue);
      });
    }
  }

  resetFiltersClientSide() {
    this.tableColumns.map((item: any) => {
      if (item.filterType === "dropdown" && item?.selectedFilterValues) {
        item.selectedFilterValues = [];
      }
    });
    this.tableData = [...this.allData]; // restore full dataset
  }

  updatePagination() {
    if (this.isClientSide) {
      this.handlePaginationData();
    } else {
      this.handlePaginationEmitAction();
    }
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.pageSize = event.rows;
    this.updatePagination();
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage = this.currentPage - 1;
      this.updatePagination();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage = this.currentPage + 1;
      this.updatePagination();
    }
  }

  onPageInputChange(event: any) {
    const inputPage = parseInt(event.target.value, 10);
    if (!isNaN(inputPage) && inputPage >= 1 && inputPage <= this.totalPages) {
      this.currentPage = inputPage;
      this.first = (inputPage - 1) * this.pageSize;
      this.updatePagination();
    }
  }

  handlePaginationEmitAction() {
    let option = {
      key: "pagination",
      callBack: "paginationAction",
    };

    let paginationInput = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    this.emitTableAction.emit({ action: option, data: paginationInput });
  }

  // Helper method to get the StatusOptions to update the status
  getDropdownOptions(dataStatus: string): any[] {
    if (this.isClientSide) return this.statusOptions;

    const isExtraStatus = this.needsExtraStatus.includes(dataStatus);
    const exists = this.statusOptions.some(
      (opt: any) => opt.value === dataStatus
    );

    if (isExtraStatus && !exists) {
      // Add the missing status temporarily to the options
      return [
        {
          label: this.formatLabel(dataStatus),
          value: dataStatus,
        },
        ...this.statusOptions,
      ];
    }

    return this.statusOptions;
  }

  // Helper method to format the status label
  formatLabel(value: string): string {
    return this.stateService.titleRemoveUnderscore(value) || value;
  }

  statusUpdate(event: any, rowData: any) {
    let option = {
      key: "status",
      callBack: "experimentStatusAction",
    };

    this.emitTableAction.emit({ action: option, data: rowData });
  }

  actionCallback(action: any, rowData: any) {
    this.emitTableAction.emit({ action: action, data: rowData });
  }

  getOwnerName(ownerBy: any) {
    return ownerBy ? ownerBy?.first_name + " " + ownerBy?.last_name : "";
  }

  toggleAccordionRow(row: any, event: Event) {
    event.stopPropagation();
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
      const action = {
        key: "",
        callBack: "getPlansForExperiment",
      };
      this.emitTableAction.emit({
        action: action,
        data: row,
      });
    }
  }

  isRowExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  getImageUrl(variantKey: string, variantsData: any): string {
    const imageData = variantsData?.[variantKey];
    const imagePath = imageData?.front || imageData?.side || imageData?.top;

    return this.imageUrlPrefix + imagePath;
  }

  getSkuImageUrl(rowData: any): string {
    const defaultImageUrl = "/assets/images/image-placeholder.svg";

    // Check variant-specific image
    if (rowData.sku_image) {
      return rowData.sku_image;
    }

    // Fallback to placeholder
    return defaultImageUrl;
  }

  onSkuImageError(event: Event) {
    // If image load fails fallback to placeholder
    (event.target as HTMLImageElement).src =
      "/assets/images/image-placeholder.svg";
  }

  getValueColor(value: any, column: any, index: number): string {
    const customColor = column.colorValues?.[index];
    if (customColor) return customColor;

    // Fallback logic: Green if > 0, else Red
    const numericValue = Number(value);
    if (numericValue >= 0) {
      return "#6EBC14"; // Green
    } else {
      return "#DC3545"; // Red
    }
  }

  getAbsoluteValue(value: any): number | string {
    if (typeof value === "string") {
      // Remove + or - sign manually
      const updatedValue = value.replace(/^-/, "");
      return updatedValue;
    }
    return Math.abs(value);
  }

  titleAddUnderscore(value: string): string {
    return value ? value.replace(/\s+/g, "_").toLowerCase() : "";
  }
}
